import { FormEvent, useEffect, useRef, useState } from "react";
import { UploadFileInfo } from "@progress/kendo-react-upload";
import { useTranslation } from "react-i18next";
import styles from "styles.module.css";

import { useUserOrganizationsStore } from "store/user";

import { usePageAccess } from "hooks/usePageAccess";
import { IVesselDocument } from "interface/vessel";
import { useParams } from "react-router";
import {
	deleteVesselDocumentApi,
	getVesselDocumentsApi,
	uploadVesselDocumentApi,
	VesselDocumentFilterOptions,
} from "api/vessel";
import Filter from "./Filter";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { DocumentsGridColumnItem } from "./GridColumns";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import DocumentFileUpload, { openFile } from "./DocumentFileUpload";
import Loading from "ui/Loading";
import { CustomInput } from "ui/Input";
import CustomButton from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";
import { useInfoStore } from "store/error";
import { InfoPros } from "interface/index";
import { infoHandler } from "./utilities/helper";

export default function VesselDocuments() {
	const { t } = useTranslation();
	const [files, setFiles] = useState<UploadFileInfo[]>([]);

	const organization = useUserOrganizationsStore((state) => state.defaultOrg);
	const [filterOptions, setFilterOptions] =
		useState<VesselDocumentFilterOptions>({
			vesselId: "",
			vesselRegistrationId: "",
		});
	const [documents, setDocuments] = useState<IVesselDocument[]>([]);
	const [documentsReady, setDocumentsReady] = useState(false);
	const [description, setDescription] = useState("");
	const deletingVessel = useRef<IVesselDocument | null>(null);

	const excelRef = useRef<ExcelExport>(null);

	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [isUploading, setIsUploading] = useState(false);

	const { id, action: type } = useParams<{ id: string; action: string }>();

	const { pageAccess, loading: pageAccessLoading } = usePageAccess();
	const { setError: setInfoError, setInfo, resetStore } = useInfoStore();
	const viewDocumentRef = useRef<IVesselDocument | null>(null);

	useEffect(() => {
		const updateMobileView = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		updateMobileView();
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		const filter = {
			vesselId: "", // organization.vesselId,
			vesselRegistrationId: "", // organization.vesselRegistrationNumber,
		};

		if (!id || !type) {
			if (organization) {
				filter.vesselId = organization.vesselId;
				filter.vesselRegistrationId = organization.vesselRegistrationNumber;
			}
		} else {
			filter.vesselId = id;
			filter.vesselRegistrationId = type;
		}

		setFilterOptions(filter);
	}, [organization, id, type]);

	useEffect(() => {
		let cancelled = false;
		setDocumentsReady(false);
		setDocuments([]);
		setFiles([]);
		if (
			!filterOptions.vesselId ||
			!filterOptions.vesselRegistrationId ||
			!pageAccess ||
			pageAccess?.read === false
		) {
			return;
		}

		const getDocuments = async () => {
			try {
				const data = await getVesselDocumentsApi(filterOptions);
				if (!cancelled) {
					setDocuments(data);
					setDocumentsReady(true);
				}
			} catch (error: any) {
				let info = infoHandler(
					"Error",
					t("UploadError"),
					t("UploadErrorMessage"),
				);
				if (error?.status === 403) {
					info.title = t("AccessDenied");
					info.description = t("UploadAccessDeniedMessage");
				}
				setInfo(info);
				console.error(error);
			}
		};
		getDocuments();
		return () => {
			cancelled = true;
		};
	}, [pageAccess, filterOptions]);

	// const exportGridAsExcel = () => {
	// 	if (excelRef.current !== null) {
	// 		excelRef.current.save();
	// 	}
	// };

	const uploadFile = async (file: File) => {
		setIsUploading(true);

		try {
			await uploadVesselDocumentApi(file, filterOptions, description);
			setFiles([]);
			setInfo({
				title: t("UploadSuccess"),
				show: true,
				description: t("UploadSuccessMessage"),
				infoType: "Success",
				actions: [
					{
						text: t("OK"),
						btn: () => resetStore(),
						disabled: false,
						bgColor: "",
						textColor: "",
					},
				],
			});
		} catch (error: any) {
			let info = infoHandler(
				"Error",
				t("UploadError"),
				t("UploadErrorMessage"),
			);
			if (error?.status === 403) {
				info.title = t("AccessDenied");
				info.description = t("UploadAccessDeniedMessage");
			}
			setInfo(info);
			console.error(error);
		} finally {
			setIsUploading(false);
		}
	};

	const handleUpload = async () => {
		if (!pageAccess?.create || !documentsReady || isUploading) {
			return;
		}
		if (files.length === 0) {
			return;
		}
		const file = files[0]?.getRawFile?.();
		if (!file || file.size === 0) {
			setInfo({
				title: t("EmptyFile"),
				show: true,
				description: t("AttachmentEmptyFile"),
				infoType: "Error",
				actions: [],
			});
			return;
		}
		if (file) {
			const existingFileName = documents.find(
				(document) => document.fileName?.trim().localeCompare(file.name) === 0,
			);

			if (existingFileName) {
				const message = `${t("FileNameExistsError")}
                                  ${t("Name")}: ${existingFileName?.fileName} 
                                  ${t("Description")}: ${existingFileName?.description ?? ""}
                                  `;
				setInfo(infoHandler("Error", t("UploadError"), message));
				return;
			}
			await uploadFile(file);
			const data = await getVesselDocumentsApi(filterOptions);
			setDocuments(data);
		}
	};

	const deleteDocument = async (documentId: string) => {
		const fileName =
			documents.find((doc) => doc.id === documentId)?.fileName ?? "";
		try {
			await deleteVesselDocumentApi({
				...filterOptions,
				documentId: documentId,
				fileName,
			});
			setInfo({
				title: t("Success"),
				show: true,
				description: t("DocumentDeleteSuccess", {
					name: fileName,
				}),
				infoType: "Success",
				actions: [
					{
						text: t("OK"),
						btn: () => resetStore(),
						disabled: false,
						bgColor: COLOR.StatusGreen,
						textColor: "white",
					},
				],
			});
			const data = await getVesselDocumentsApi(filterOptions);
			setDocuments(data);
		} catch (error) {
			console.error(error);
		}
	};
	const handleGetVesselDocumentFile = async (
		doc: IVesselDocument,
		view: boolean,
	) => {
		const fileWindow = view ? window.open("about:blank", "_blank") : null;
		if (view && !fileWindow) {
			setInfo({
				title: t("Error"),
				show: true,
				description: t("AttachmentPopupBlocked"),
				infoType: "Error",
				actions: [],
			});
			return;
		}
		if (fileWindow) fileWindow.opener = null;
		try {
			const data = await getVesselDocumentsApi({
				...filterOptions,
				documentId: doc.id,
				includeContent: 1,
			});
			if (!data || data.length === 0 || !data[0].fileByte) {
				setInfo({
					title: t("DocumentNotFound"),
					show: true,
					description: t("RequestedDocumentCouldNotBeFound"),
					infoType: "Error",
					actions: [
						{
							text: t("OK"),
							btn: () => resetStore(),
							disabled: false,
							bgColor: "",
							textColor: "",
						},
					],
				});
				fileWindow?.close();
				return null;
			}
			viewDocumentRef.current = data[0];
			const opened = openFile(viewDocumentRef.current, view, (data) => {
				if (fileWindow && !fileWindow.closed) {
					fileWindow.location.replace(data.url);
					const cleanup = window.setInterval(() => {
						if (fileWindow.closed) {
							URL.revokeObjectURL(data.url);
							window.clearInterval(cleanup);
						}
					}, 1000);
				} else {
					URL.revokeObjectURL(data.url);
				}
			});
			if (!opened) fileWindow?.close();
		} catch (error: any) {
			fileWindow?.close();
			const info: InfoPros = {
				title: t("Error"),
				show: true,
				description: t("AttachmentDownloadError"),
				infoType: "Error",
				actions: [
					{
						text: t("Cancel"),
						btn: () => resetStore(),
						disabled: false,
						bgColor: "",
						textColor: "",
					},
					{
						text: t("OK"),
						btn: () => resetStore(),
						disabled: false,
						bgColor: "",
						textColor: "",
					},
				],
			};
			setInfo(infoHandler("Error", t("Error"), t("AttachmentDownloadError")));
			if (error?.status === 403) {
				info.title = t("AccessDenied");
				info.description = t("UploadAccessDeniedMessage");
			}
			setInfo(info);
			console.error(error);
		}
	};
	const handleDeleteDocument = (document: IVesselDocument) => {
		deletingVessel.current = document;
		setInfo({
			title: t("DeleteDocument"),
			show: true,
			description: t("ConfirmDocumentDelete", { name: document.fileName }),
			infoType: "Warning",
			actions: [
				{
					text: t("Cancel"),
					btn: () => resetStore(),
					disabled: false,
					bgColor: "",
					textColor: "",
				},
				{
					text: t("Delete"),
					btn: async () => {
						resetStore();
						await deleteDocument(document.id);
						deletingVessel.current = null;
					},
					disabled: false,
					bgColor: "",
					textColor: "",
				},
			],
		});
	};

	if (pageAccessLoading || !pageAccess) {
		return <Loading show={true} />;
	}

	const hasWriteAccess = pageAccess?.create ?? false;
	const showUpload =
		hasWriteAccess &&
		filterOptions.vesselId &&
		filterOptions.vesselRegistrationId;

	return (
		<div
			className={styles.PageContainer}
			style={{ height: "auto", minHeight: "calc(100% - 90px)" }}
		>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{`${t("VesselDocuments")} => ${filterOptions.vesselId || ""} | ${filterOptions.vesselRegistrationId || ""} `}
				</legend>
			</div>
			{pageAccess?.read && (
				<div style={{ marginTop: "20px" }}>
					<Filter
						setFilterOptions={setFilterOptions}
						filterOptions={filterOptions}
						// exportAsExcel={exportGridAsExcel}
					/>
				</div>
			)}

			{showUpload && (
				<>
					<DocumentFileUpload
						key={`${filterOptions.vesselId}:${filterOptions.vesselRegistrationId}`}
						files={files}
						setFiles={(newFiles) => {
							if (files.length > 0 && description) {
								setDescription("");
							}

							setFiles(newFiles);
						}}
						disabled={isUploading || !documentsReady}
					/>
					<div style={{ marginTop: "30px", marginBottom: "10px" }}>
						<CustomInput
							multiple={true}
							value={description}
							onChange={(value) => {
								setDescription(value);
							}}
							label={t("Description")}
							placeholder={t("Description")}
						/>

						<div style={{ marginTop: "20px" }}>
							<CustomButton
								iconName={ICON_NAMES.Upload}
								onClick={handleUpload}
								loading={isUploading}
								disabled={
									isUploading ||
									!documentsReady ||
									files.length === 0 ||
									!pageAccess?.create
								}
								label={t("Upload")}
								// bgColor={COLOR.StatusGreen}
								iconRight={false}
							/>
						</div>
					</div>
				</>
			)}
			{pageAccess?.read ? (
				<>
					<GridWithGroupPaging
						dataKey="id"
						gridColumns={DocumentsGridColumnItem()}
						filter={filterOptions}
						gridData={documents}
						unUsedHeight={880}
						// pageAccess={pageAccess}
						// exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						// lastColWidth="90px"
						// apiMethod={getVesselDocumentsApi}
						pageable={false}
						excelProps={{
							fileName: `documents_${new Date().toISOString()}.xlsx`,
						}}
						lastColBtns={{
							field: "",
							title: "",
							headerClassName: "no-border",
							width: pageAccess?.delete ? "170px" : "140px",
							cells: {
								data: (props) => {
									const dataItem = props.dataItem;
									return (
										<td {...props.tdProps}>
											<div style={{ display: "flex", gap: "10px" }}>
												{pageAccess?.delete && (
													<CustomButton
														iconName={ICON_NAMES.Delete}
														onClick={() => handleDeleteDocument(dataItem)}
														label={""}
														btnStyle={{ width: "40px" }}
														// iconRight={false}
													/>
												)}

												<CustomButton
													iconName={ICON_NAMES.Visibility}
													onClick={() =>
														handleGetVesselDocumentFile(dataItem, true)
													}
													label={""}
													btnStyle={{ width: "40px" }}

													// iconRight={false}
												/>

												<CustomButton
													iconName={ICON_NAMES.Download}
													onClick={() =>
														handleGetVesselDocumentFile(dataItem, false)
													}
													label={""}
													btnStyle={{ width: "40px" }}

													// iconRight={false}
												/>
											</div>
										</td>
									);
								},
							},
						}}
					/>
					<div style={{ marginTop: "20px", textAlign: "center" }}>
						{documents.length === 0 && <p>{t("NoVesselDocuments")}</p>}
					</div>
				</>
			) : (
				<div
					className="no-access"
					style={{ marginTop: "60px", textAlign: "center" }}
				>
					<h1>{t("AccessDenied")}</h1>
					<p>{t("AccessDeniedDescription")}</p>
					<p>{t("AccessDeniedAction")}</p>
				</div>
			)}
		</div>
	);
}
